import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, SkipForward, Sparkles, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "./shared";

export default function ApprovalQueue({ approvals, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);

  const approve = async (a) => {
    setBusy(a.id);
    try {
      const body = editing === a.id ? { subject: draft.subject, body: draft.body } : {};
      const { data } = await api.post(`/approvals/${a.id}/approve`, body);
      toast[data.outcome === "failed" ? "error" : "success"](data.outcome === "sent" ? `Sent to ${a.contact_name}` : data.outcome === "deferred" ? "Approved — will send when the window opens / cap frees up" : "Send failed — see enrollment error");
      setEditing(null); onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };
  const skip = async (a) => {
    setBusy(a.id);
    try { await api.post(`/approvals/${a.id}/skip`); toast.success(`Skipped step ${a.step_index + 1} for ${a.contact_name}`); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="border border-amber-500/30 rounded-md bg-amber-500/5" data-testid="approval-queue">
      <div className="px-4 py-2 border-b border-amber-500/20 text-xs flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-amber-500" /><span className="font-medium">Human-in-the-loop</span><span className="text-muted-foreground">— {approvals.length} draft{approvals.length === 1 ? "" : "s"} waiting for your approval before sending</span></div>
      <div className="divide-y divide-border">
        {approvals.map((a) => (
          <div key={a.id} className="p-4 space-y-2" data-testid={`approval-${a.id}`}>
            <div className="flex items-center justify-between gap-3 text-xs">
              <div><span className="font-medium text-sm">{a.contact_name}</span> <span className="mono text-muted-foreground">{a.contact_email}{a.company_name ? ` · ${a.company_name}` : ""}</span></div>
              <div className="flex items-center gap-2 text-muted-foreground mono">step {a.step_index + 1}{a.ai_personalized && <span className="inline-flex items-center gap-1 text-primary"><Sparkles className="w-3 h-3" />AI</span>}<span>{fmtTime(a.created_at)}</span></div>
            </div>
            {editing === a.id ? (
              <div className="space-y-2"><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="h-8 text-sm" data-testid={`approval-subject-${a.id}`} /><Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={6} className="text-sm" data-testid={`approval-body-${a.id}`} /></div>
            ) : (
              <div className="border border-border rounded-md bg-card px-3 py-2"><div className="text-sm font-medium" data-testid={`approval-preview-subject-${a.id}`}>{a.subject}</div><div className="text-xs text-muted-foreground whitespace-pre-wrap mt-1 line-clamp-6">{a.body}</div></div>
            )}
            <div className="flex justify-end gap-2">
              {editing === a.id ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditing(null)}>Cancel edit</Button> : <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditing(a.id); setDraft({ subject: a.subject, body: a.body }); }} data-testid={`approval-edit-${a.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>}
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy === a.id} onClick={() => skip(a)} data-testid={`approval-skip-${a.id}`}><SkipForward className="w-3 h-3 mr-1" />Skip step</Button>
              <Button size="sm" className="h-7 text-xs" disabled={busy === a.id} onClick={() => approve(a)} data-testid={`approval-approve-${a.id}`}><Check className="w-3 h-3 mr-1" />{busy === a.id ? "Sending..." : "Approve & send"}</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
